import { useEffect, useMemo, useState } from "react";
import { Alert } from "@/components/ui/Alert";
import { Button } from "@/components/ui/Button";
import { Dialog } from "@/components/ui/Dialog";
import { BestellnummerField } from "@/components/BestellnummerField";
import { Spinner } from "@/components/ui/Spinner";
import { ApiError } from "@/lib/apiClient";
import { formatCurrency } from "@/lib/documentCalc";
import { toast } from "@/lib/toast";
import {
  convertDokument,
  DOKUMENT_TYP_LABELS,
  dokumentDisplayTitle,
  fetchBestellnummerVorschlag,
  fetchDocumentChain,
  getDokument,
  listLinkedAuftragsbestaetigungen,
  type DocumentChain,
  type DokumentDetail,
  type DokumentPosition,
} from "@/lib/dokumente";
import {
  AngebotPositionAuswahlDialog,
  buildAuftragsbestaetigungPositionen,
} from "@/pages/dokumente/AngebotPositionAuswahlDialog";
import { defaultChainTabKey, DocumentChainPanel } from "@/pages/dokumente/DocumentChainPanel";
import { useDocumentEditorLayers } from "@/pages/dokumente/documentEditorLayers";
import { isOptionalOrAlternativPosition } from "@/pages/dokumente/documentPositionModel";
import { useDocumentEditor } from "@/pages/dokumente/useDocumentEditor";

/** Headless-UI: Ziel-Dialog erst nach Leave-Transition des Bestellnummer-Modals öffnen. */
const OPEN_EDITOR_AFTER_CLOSE_MS = 180;

export interface AuftragsbestaetigungFromAngebotDialogProps {
  open: boolean;
  angebotId: number | null;
  onClose: () => void;
}

export function AuftragsbestaetigungFromAngebotDialog({
  open,
  angebotId,
  onClose,
}: AuftragsbestaetigungFromAngebotDialogProps) {
  const { open: openDocumentEditor } = useDocumentEditor();
  const layers = useDocumentEditorLayers();
  const [angebot, setAngebot] = useState<DokumentDetail | null>(null);
  const [chain, setChain] = useState<DocumentChain | null>(null);
  const [loading, setLoading] = useState(false);
  const [creating, setCreating] = useState(false);
  const [error, setError] = useState<string | null>(null);
  const [bestellnummer, setBestellnummer] = useState("");
  const [bestellnummerBaseline, setBestellnummerBaseline] = useState("");
  const [chainTabKey, setChainTabKey] = useState("");
  const [positionAuswahlOpen, setPositionAuswahlOpen] = useState(false);

  useEffect(() => {
    if (!open || angebotId == null || !Number.isFinite(angebotId)) {
      setAngebot(null);
      setChain(null);
      setLoading(false);
      setCreating(false);
      setError(null);
      setBestellnummer("");
      setBestellnummerBaseline("");
      setChainTabKey("");
      setPositionAuswahlOpen(false);
      return;
    }

    let cancelled = false;
    setLoading(true);
    setError(null);
    void (async () => {
      try {
        const [detail, chainData] = await Promise.all([
          getDokument(angebotId),
          fetchDocumentChain(angebotId),
        ]);
        if (cancelled) return;
        if (detail.dokument_typ !== "angebot") {
          setError("Nur Angebote können in Auftragsbestätigungen umgewandelt werden.");
          setAngebot(null);
          setChain(null);
          return;
        }
        setAngebot(detail);
        setChain(chainData);
        setChainTabKey(defaultChainTabKey(chainData, true));
        const suggested =
          chainData.bestellnummer?.trim() ||
          (await fetchBestellnummerVorschlag(angebotId).catch(() => ({ bestellnummer: null })))
            .bestellnummer?.trim() ||
          "";
        if (!cancelled) {
          setBestellnummer(suggested);
          setBestellnummerBaseline(suggested);
        }
      } catch (err) {
        if (!cancelled) {
          setError(
            err instanceof ApiError ? err.message : "Angebot konnte nicht geladen werden.",
          );
          setAngebot(null);
          setChain(null);
        }
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();

    return () => {
      cancelled = true;
    };
  }, [angebotId, open]);

  const existingLinks = useMemo(
    () => (angebot ? listLinkedAuftragsbestaetigungen(angebot.verknuepfungen, angebot.id) : []),
    [angebot],
  );

  const specialPositions = useMemo(
    () => (angebot?.positionen || []).filter((row) => isOptionalOrAlternativPosition(row)),
    [angebot],
  );

  function openCreatedEditor(docId: number) {
    setPositionAuswahlOpen(false);
    onClose();
    window.setTimeout(() => {
      openDocumentEditor({ docId });
    }, OPEN_EDITOR_AFTER_CLOSE_MS);
  }

  async function createAuftragsbestaetigung(positionen?: DokumentPosition[]) {
    if (!angebot) return;
    setCreating(true);
    setError(null);
    try {
      const created = await convertDokument(angebot.id, "auftragsbestaetigung", {
        bestellnummer: bestellnummer.trim() || null,
        positionen,
      });
      toast.success(`${DOKUMENT_TYP_LABELS.auftragsbestaetigung} erstellt.`);
      openCreatedEditor(created.id);
    } catch (err) {
      setError(
        err instanceof ApiError
          ? err.message
          : "Auftragsbestätigung konnte nicht erstellt werden.",
      );
    } finally {
      setCreating(false);
    }
  }

  function handleCreateClick() {
    if (!angebot) return;
    if (specialPositions.length > 0) {
      setPositionAuswahlOpen(true);
      return;
    }
    void createAuftragsbestaetigung();
  }

  function handlePositionAuswahlConfirm(acceptedIds: number[]) {
    if (!angebot) return;
    const positionen = buildAuftragsbestaetigungPositionen(
      angebot.positionen || [],
      acceptedIds,
    );
    void createAuftragsbestaetigung(positionen);
  }

  function handleOpenExisting(zielDokumentId: number) {
    openCreatedEditor(zielDokumentId);
  }

  return (
    <>
      <Dialog
        open={open}
        onClose={creating ? () => undefined : onClose}
        title="Bestellnummer und Auftragsbestätigung"
        description="Alle zugehörigen Dokumente in der Workflow-Reihenfolge — Bestellnummer eintragen und Auftragsbestätigung erstellen."
        size="4xl"
        fullScreenMobile
        zIndexClass={layers.ab}
        panelClassName="max-h-[95vh] w-[min(100vw-1rem,96rem)]"
        footer={
          <div className="flex flex-wrap justify-end gap-2">
            <Button
              type="button"
              onClick={handleCreateClick}
              disabled={loading || creating || !angebot}
              loading={creating && !positionAuswahlOpen}
            >
              {specialPositions.length > 0
                ? "Weiter zur Positionsauswahl"
                : "Auftragsbestätigung erstellen"}
            </Button>
          </div>
        }
      >
        {error ? <Alert variant="error">{error}</Alert> : null}

        {loading ? (
          <div className="flex items-center gap-2 py-6 text-sm text-label-secondary">
            <Spinner />
            Angebot wird geladen …
          </div>
        ) : angebot && chain ? (
          <div className="grid gap-5 lg:grid-cols-[minmax(0,1.45fr)_minmax(18rem,0.55fr)]">
            <DocumentChainPanel
              chain={chain}
              activeKey={chainTabKey || chain.current_key}
              onTabChange={setChainTabKey}
              currentDocId={angebot.id}
            />

            <div className="space-y-4">
              <div className="rounded-ios border border-separator bg-fill-quaternary/20 px-4 py-3">
                <p className="text-sm font-medium text-label">{dokumentDisplayTitle(angebot)}</p>
                {angebot.kunde_name ? (
                  <p className="mt-1 text-sm text-label-secondary">{angebot.kunde_name}</p>
                ) : null}
                <p className="mt-1 text-sm tabular-nums text-label-secondary">
                  {formatCurrency(angebot.summe_brutto, angebot.waehrung)}
                </p>
              </div>

              <BestellnummerField
                value={bestellnummer}
                onChange={setBestellnummer}
                regelWert={bestellnummerBaseline}
                placeholder="z. B. PO-2025-00123"
                onApplyRegel={() => setBestellnummer(bestellnummerBaseline)}
              />

              <p className="text-sm text-label-secondary">
                Kopfdaten und Standardpositionen werden aus dem angenommenen Angebot übernommen.
                {specialPositions.length > 0
                  ? ` ${specialPositions.length} optionale/alternative Positionen wählen Sie im nächsten Schritt aus.`
                  : null}
              </p>

              {existingLinks.length > 0 ? (
                <Alert variant="info" title="Bereits vorhandene Auftragsbestätigung">
                  <div className="space-y-2">
                    <p>Für dieses Angebot existiert bereits mindestens eine Auftragsbestätigung.</p>
                    <div className="flex flex-wrap gap-2">
                      {existingLinks.map((link) => (
                        <Button
                          key={link.id}
                          type="button"
                          variant="secondary"
                          size="sm"
                          onClick={() => handleOpenExisting(link.ziel_dokument_id)}
                          disabled={creating}
                        >
                          {link.ziel_nummer || `AB #${link.ziel_dokument_id}`} öffnen
                        </Button>
                      ))}
                    </div>
                  </div>
                </Alert>
              ) : null}
            </div>
          </div>
        ) : null}
      </Dialog>

      <AngebotPositionAuswahlDialog
        open={positionAuswahlOpen}
        positions={angebot?.positionen || []}
        currency={angebot?.waehrung || "EUR"}
        confirming={creating}
        zIndexClass={layers.abNested}
        onClose={() => {
          if (!creating) setPositionAuswahlOpen(false);
        }}
        onConfirm={handlePositionAuswahlConfirm}
      />
    </>
  );
}
